import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import CreateVMModal from './orka/CreateVMModal';
import { setOrkaTokenGetter, getImages, startImageBuild, getImageBuildJob } from './orka/api';
import './RunBuild.css';

function RunBuild({ teamcityApi, buildConfigs, isAdmin, watchedBuilds, setWatchedBuilds, onBack, initialSettings }) {
  const [buildTypes, setBuildTypes] = useState([]);
  const [allowedBuilds, setAllowedBuilds] = useState([]);
  const [adminBuilds, setAdminBuilds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [buildMachines, setBuildMachines] = useState([]); // Orka VMs correlated to TC agents
  const [selectedVM, setSelectedVM] = useState(null); // when set, pins the build to this VM's agent
  const [showDeployModal, setShowDeployModal] = useState(false);

  // CreateVMModal needs a react-query context + an authed Orka client. Both are
  // otherwise only set up in OrkaDashboard, so wire them here for direct deploys.
  const { getToken } = useAuth();
  const [orkaQueryClient] = useState(() => new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
  }));
  useEffect(() => {
    setOrkaTokenGetter(getToken);
  }, [getToken]);

  // Reload just the build-machine list (after deploying a VM).
  const loadBuildMachines = useCallback(async () => {
    try {
      const result = await teamcityApi.getBuildMachines();
      setBuildMachines(result?.items || []);
    } catch (err) {
      console.error('Failed to refresh build machines:', err);
    }
  }, [teamcityApi]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('build'); // 'build' | 'parameters'

  // Form state
  const [selectedBuildType, setSelectedBuildType] = useState('');
  const [shelvedChangelists, setShelvedChangelists] = useState([]);
  const [selectedShelvedCL, setSelectedShelvedCL] = useState('');
  const [shelvedCLDescription, setShelvedCLDescription] = useState('');
  const [loadingShelved, setLoadingShelved] = useState(false);
  const [baseCL, setBaseCL] = useState(''); // empty = latest changes
  const [baseCLDescription, setBaseCLDescription] = useState('');
  const [baseCLUser, setBaseCLUser] = useState('');
  const [baseCLInputFocused, setBaseCLInputFocused] = useState(false);
  const [recentChangelists, setRecentChangelists] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [showBaseCLDropdown, setShowBaseCLDropdown] = useState(false);
  const [showCLDropdown, setShowCLDropdown] = useState(false);
  const [clFilterText, setCLFilterText] = useState(''); // Separate filter text for searching
  const [clInputFocused, setCLInputFocused] = useState(false); // Track if CL input is focused

  // Refs for click outside handling
  const baseCLComboboxRef = useRef(null);

  const clComboboxRef = useRef(null);
  // Holds a re-run snapshot until its build type is selected, so it can override
  // the per-user last-used prefill (see the selectedBuildType effect below).
  const pendingPrefillRef = useRef(null);
  const [selectedAgent, setSelectedAgent] = useState(''); // empty = any agent
  const [notifyOnComplete, setNotifyOnComplete] = useState(true);

  // Parameters state
  const [parameters, setParameters] = useState([]);
  const [customParameters, setCustomParameters] = useState({});
  const [loadingParameters, setLoadingParameters] = useState(false);

  // Image-build state (admin-only)
  const [availableImages, setAvailableImages] = useState([]);
  const [baseImage, setBaseImage] = useState('');
  const [imageBuildSubmitting, setImageBuildSubmitting] = useState(false);
  const [imageBuildJobId, setImageBuildJobId] = useState(null);
  const [imageBuildJob, setImageBuildJob] = useState(null);

  // VCS roots state (for stream filtering)
  const [vcsRoots, setVcsRoots] = useState([]);
  const [favoriteParams, setFavoriteParams] = useState(() => {
    const saved = localStorage.getItem('favoriteParams');
    return saved ? JSON.parse(saved) : [];
  });

  // Load build types and agents on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Refresh agents every 10 seconds to show current status
  useEffect(() => {
    const interval = setInterval(loadAgents, 10000);
    return () => clearInterval(interval);
  }, []);

  // Load available Harbor images for the admin base-image selector.
  useEffect(() => {
    if (!isAdmin) return;
    getImages()
      .then(res => setAvailableImages(res.data?.items || []))
      .catch(err => console.error('Failed to load images for base-image selector:', err));
  }, [isAdmin]);

  // Poll the image-build job every 3s until it reaches a terminal state.
  useEffect(() => {
    if (!imageBuildJobId) return;
    const TERMINAL = new Set(['done', 'failed']);
    let cancelled = false;

    const poll = () => {
      if (cancelled) return;
      getImageBuildJob(imageBuildJobId)
        .then(res => {
          if (cancelled) return;
          const job = res.data;
          setImageBuildJob(job);
          if (!TERMINAL.has(job.step)) {
            setTimeout(poll, 3000);
          }
        })
        .catch(() => {
          if (!cancelled) setTimeout(poll, 3000);
        });
    };

    poll();
    return () => { cancelled = true; };
  }, [imageBuildJobId]);

  async function handleStartImageBuild() {
    setImageBuildSubmitting(true);
    try {
      const res = await startImageBuild({
        buildTypeId: selectedBuildType,
        baseImage,
        changelist: selectedShelvedCL || undefined,
        baseCL: baseCL || undefined,
        customParameters: Object.keys(customParameters).length ? customParameters : undefined,
      });
      const jobId = res.data.jobId;
      setImageBuildJobId(jobId);
      setImageBuildJob({ step: 'queued' });
    } catch (err) {
      setError('Failed to start image build: ' + (err.message || 'Unknown error'));
    } finally {
      setImageBuildSubmitting(false);
    }
  }

  async function loadInitialData() {
    try {
      setLoading(true);
      const [types, agentList, allowedResult, machinesResult] = await Promise.all([
        teamcityApi.getAllBuildTypes(),
        teamcityApi.getAgents(),
        teamcityApi.getAllowedBuilds().catch(err => {
          console.error('Failed to load allowed builds:', err);
          return { allowedBuilds: [] };
        }),
        teamcityApi.getBuildMachines().catch(err => {
          console.error('Failed to load build machines (Orka):', err);
          return { items: [] };
        })
      ]);
      setBuildTypes(types);
      setAgents(agentList);
      const allowed = allowedResult?.allowedBuilds || [];
      setAllowedBuilds(allowed);
      setAdminBuilds(allowedResult?.adminBuilds || []);
      setBuildMachines(machinesResult?.items || []);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadAgents() {
    try {
      const agentList = await teamcityApi.getAgents();
      setAgents(agentList);
    } catch (err) {
      console.error('Failed to refresh agents:', err);
    }
  }

  // Load parameters and VCS roots when build type changes. A pending re-run
  // snapshot (initialSettings) overrides the per-user last-used prefill.
  useEffect(() => {
    if (selectedBuildType) {
      loadBuildParameters(selectedBuildType);
      loadVcsRoots(selectedBuildType);
      const pending = pendingPrefillRef.current;
      if (pending && pending.buildTypeId === selectedBuildType) {
        applyPrefill(pending);
        pendingPrefillRef.current = null;
      } else {
        loadLastUsed(selectedBuildType);
      }
    } else {
      setParameters([]);
      setCustomParameters({});
      setVcsRoots([]);
    }
  }, [selectedBuildType]);

  // A re-run hands in initialSettings: stash the snapshot and select its build
  // type, which triggers the effect above to apply the snapshot.
  useEffect(() => {
    if (initialSettings && initialSettings.buildTypeId) {
      pendingPrefillRef.current = initialSettings;
      setSelectedBuildType(initialSettings.buildTypeId);
    }
  }, [initialSettings]);

  // Apply a re-run snapshot to the form. The live param list still loads via
  // loadBuildParameters, so the snapshot's customParameters overlay current defaults.
  function applyPrefill(snap) {
    setCustomParameters(snap.customParameters || {});
    setBaseCL(snap.baseCL || '');
    setBaseCLDescription('');
    setBaseCLUser('');
    setSelectedShelvedCL(snap.shelvedChangelist || '');
    const agentId = snap.agentId != null && snap.agentId !== '' ? String(snap.agentId) : '';
    setSelectedAgent(agentId);
    // Best-effort: if a deployed VM maps to that agent, pin the build to it.
    if (agentId) {
      const vm = buildMachines.find(
        (m) => m.agent && String(m.agent.id) === agentId
      );
      if (vm) setSelectedVM(vm);
    }
  }

  async function loadBuildParameters(buildTypeId) {
    setLoadingParameters(true);
    try {
      const result = await teamcityApi.getBuildParameters(buildTypeId);
      setParameters(result.parameters || []);
    } catch (err) {
      console.error('Failed to load build parameters:', err);
      setParameters([]);
    } finally {
      setLoadingParameters(false);
    }
  }

  // Load the user's last-used settings for this build and pre-fill the form
  async function loadLastUsed(buildTypeId) {
    try {
      const saved = await teamcityApi.getLastUsed(buildTypeId);
      setCustomParameters(saved.customParameters || {});
      setBaseCL(saved.baseCL || '');
      setBaseCLDescription('');
      setBaseCLUser('');
      setSelectedAgent(saved.agentId || '');
    } catch (err) {
      console.error('Failed to load last-used settings:', err);
      setCustomParameters({});
    }
  }

  async function loadVcsRoots(buildTypeId) {
    try {
      const result = await teamcityApi.getVcsRoots(buildTypeId);
      setVcsRoots(result.vcsRoots || []);
    } catch (err) {
      console.error('Failed to load VCS roots:', err);
      setVcsRoots([]);
    }
  }

  // Load shelved changelists for the logged-in user, scoped to the build's branch.
  const loadShelvedChangelists = useCallback(async (path) => {
    setLoadingShelved(true);
    try {
      const cls = await teamcityApi.getShelvedChangelists(path);
      setShelvedChangelists(cls);
      // Don't auto-select - let user see the full list in dropdown
    } catch (err) {
      console.error('Failed to load shelved changelists:', err);
      setShelvedChangelists([]);
    } finally {
      setLoadingShelved(false);
    }
  }, [teamcityApi]);

  // Handle CL input change - filter the dropdown; description (for the input label)
  // comes from the matching list row.
  function handleCLInputChange(value) {
    setSelectedShelvedCL(value);
    setCLFilterText(value); // Update filter text as user types
    setShowCLDropdown(true); // Keep dropdown open while typing
    const match = shelvedChangelists.find(cl => cl.changelist === value);
    setShelvedCLDescription(match ? match.description || '' : '');
  }

  // Select shelved CL from dropdown
  function handleCLSelect(cl) {
    setSelectedShelvedCL(cl.changelist);
    setShelvedCLDescription(cl.description || '');
    setCLFilterText(''); // Clear filter when selecting
    setShowCLDropdown(false);
  }

  // Load recent changelists for "Include changes" dropdown, scoped to the build's branch.
  const loadRecentChangelists = useCallback(async (path) => {
    setLoadingRecent(true);
    try {
      const cls = await teamcityApi.getRecentChangelists(20, path);
      setRecentChangelists(cls);
    } catch (err) {
      console.error('Failed to load recent changelists:', err);
      setRecentChangelists([]);
    } finally {
      setLoadingRecent(false);
    }
  }, [teamcityApi]);

  // Handle base CL input change
  async function handleBaseCLInputChange(value) {
    setBaseCL(value);
    setShowBaseCLDropdown(false);

    // Try to find in the recent list
    const match = recentChangelists.find(cl => cl.changelist === value);
    if (match) {
      setBaseCLDescription(match.description || '');
      setBaseCLUser(match.user || '');
      return;
    }

    // If not found and looks like a valid CL number, fetch from API
    if (value && /^\d+$/.test(value)) {
      setBaseCLUser('');
      try {
        const result = await teamcityApi.getChangelist(value);
        setBaseCLDescription(result.description || '');
      } catch (err) {
        setBaseCLDescription('');
      }
    } else {
      setBaseCLDescription('');
      setBaseCLUser('');
    }
  }

  // Select base CL from dropdown
  function handleBaseCLSelect(cl) {
    setBaseCL(cl.changelist);
    setBaseCLDescription(cl.description || '');
    setBaseCLUser(cl.user || '');
    setShowBaseCLDropdown(false);
  }

  // Extract stream name from full path (e.g., "//depot/next_dev" -> "next_dev")
  function getStreamName(streamPath) {
    if (!streamPath) return null;
    // Remove leading slashes and get the last part of the path
    const parts = streamPath.replace(/^\/+/, '').split('/');
    return parts[parts.length - 1] || null;
  }

  // Check if a CL's stream is compatible with the build's VCS root stream
  // e.g., "next_dev_engineering" is compatible with "next_dev" (shares prefix)
  function isStreamCompatible(clStream, vcsRootStream) {
    if (!clStream || !vcsRootStream) return true; // If we don't have stream info, assume compatible
    const clStreamName = getStreamName(clStream);
    const vcsStreamName = getStreamName(vcsRootStream);
    if (!clStreamName || !vcsStreamName) return true;
    // Check if they share the same prefix (e.g., next_dev matches next_dev_engineering)
    return clStreamName.startsWith(vcsStreamName) || vcsStreamName.startsWith(clStreamName);
  }

  // Get the primary VCS root stream (first one, typically the main source)
  const primaryVcsStream = vcsRoots.length > 0 ? vcsRoots[0].stream : null;

  // Filter shelved list based on stream compatibility and filter text
  const streamFilteredCLs = selectedBuildType && primaryVcsStream
    ? shelvedChangelists.filter(cl => isStreamCompatible(cl.stream, primaryVcsStream))
    : shelvedChangelists;

  const filteredShelvedCLs = clFilterText
    ? streamFilteredCLs.filter(cl =>
        cl.changelist.includes(clFilterText) ||
        cl.description.toLowerCase().includes(clFilterText.toLowerCase())
      )
    : streamFilteredCLs;

  // Deduplicate and filter recent changelists based on typed input
  const uniqueRecentCLs = recentChangelists.filter(
    (cl, index, self) => index === self.findIndex(c => c.changelist === cl.changelist)
  );
  const filteredRecentCLs = baseCL
    ? uniqueRecentCLs.filter(cl =>
        cl.changelist.includes(baseCL) ||
        cl.description.toLowerCase().includes(baseCL.toLowerCase())
      )
    : uniqueRecentCLs;

  // Load shelved + recent CLs filtered to the selected build's branch (its first VCS
  // root). Re-runs when the build (and thus the primary stream) changes; clears the
  // pickers until a build is selected.
  useEffect(() => {
    if (primaryVcsStream) {
      loadShelvedChangelists(primaryVcsStream);
      loadRecentChangelists(primaryVcsStream);
    } else {
      setShelvedChangelists([]);
      setRecentChangelists([]);
    }
  }, [primaryVcsStream, loadShelvedChangelists, loadRecentChangelists]);

  // Click outside handler for CL dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (clComboboxRef.current && !clComboboxRef.current.contains(event.target)) {
        setShowCLDropdown(false);
      }
      if (baseCLComboboxRef.current && !baseCLComboboxRef.current.contains(event.target)) {
        setShowBaseCLDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Request notification permission
  async function requestNotificationPermission() {
    if (!('Notification' in window)) {
      return false;
    }
    if (Notification.permission === 'granted') {
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!selectedBuildType) {
      setError('Please select a build configuration');
      return;
    }

    const changelist = selectedShelvedCL;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Only include custom parameters that have non-empty values
      const nonEmptyCustomParams = {};
      for (const [name, value] of Object.entries(customParameters)) {
        if (value !== undefined && value !== null && value !== '') {
          nonEmptyCustomParams[name] = value;
        }
      }

      // A selected Orka VM pins the build to its TeamCity agent, overriding the dropdown.
      const effectiveAgentId = selectedVM ? selectedVM.agent.id : selectedAgent;

      const result = await teamcityApi.triggerBuild(selectedBuildType, {
        changelist: changelist || undefined,
        agentId: effectiveAgentId || undefined,
        personal: !!changelist,
        baseCL: baseCL || undefined,
        customParameters: Object.keys(nonEmptyCustomParams).length > 0 ? nonEmptyCustomParams : undefined
      });

      // If notify is enabled, watch this build
      if (notifyOnComplete && result.id) {
        const hasPermission = await requestNotificationPermission();
        if (hasPermission) {
          const buildName = buildTypes.find(b => b.id === selectedBuildType)?.name || selectedBuildType;
          setWatchedBuilds(prev => ({
            ...prev,
            [result.id]: { name: buildName, watchedAt: Date.now() }
          }));
        }
      }

      setSuccess(`Build queued successfully! Build ID: ${result.id}`);

      // Persist last-used settings (base CL, agent, parameter overrides) for next time.
      // Shelved CL is intentionally NOT saved - it's per-run, not a sticky default.
      teamcityApi.saveLastUsed(selectedBuildType, {
        baseCL: baseCL || undefined,
        agentId: selectedAgent || undefined,
        customParameters: Object.keys(nonEmptyCustomParams).length > 0 ? nonEmptyCustomParams : undefined,
      }).catch(err => console.error('Failed to save last-used settings:', err));

      // Reset form
      setSelectedShelvedCL('');
      setShelvedCLDescription('');
      setBaseCL('');
      setBaseCLDescription('');
      setBaseCLUser('');
    } catch (err) {
      setError('Failed to trigger build: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Filter build types by allowed builds (if configured)
  const filteredBuildTypes = allowedBuilds.length > 0
    ? buildTypes.filter(bt => allowedBuilds.some(ab => ab.id === bt.id))
    : buildTypes;

  console.log('Build filtering:', {
    totalBuilds: buildTypes.length,
    allowedCount: allowedBuilds.length,
    filteredCount: filteredBuildTypes.length
  });

  // Build the dropdown options. Prefer the curated allow-list (friendly stream/platform
  // labels, grouped by stream); fall back to all build types grouped by project.
  const useCurated = allowedBuilds.length > 0 && allowedBuilds.some(ab => ab.label || ab.stream);
  const curatedOptions = useCurated
    ? allowedBuilds.map(ab => ({
        id: ab.id,
        text: ab.name || ab.label || ab.platform || ab.id,
        group: ab.streamLabel || ab.stream || 'Custom Builds',
      }))
    : filteredBuildTypes.map(bt => ({
        id: bt.id,
        text: getSimpleBuildName(bt.name),
        group: bt.projectName || 'Unknown Project',
      }));

  const adminBuildOptions = adminBuilds.map(ab => ({
    id: ab.id,
    text: ab.name || ab.id,
    group: ab.projectName || 'All Builds',
  }));

  const buildOptions = [...curatedOptions, ...adminBuildOptions];

  const groupedOptions = buildOptions.reduce((acc, o) => {
    if (!acc[o.group]) {
      acc[o.group] = [];
    }
    acc[o.group].push(o);
    return acc;
  }, {});

  // Format time remaining
  function formatTimeRemaining(seconds) {
    if (!seconds || seconds <= 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s left`;
    }
    return `${secs}s left`;
  }

  // Get agent display info
  function getAgentDisplayInfo(agent) {
    const status = [];

    if (!agent.connected) {
      status.push('Disconnected');
    } else if (!agent.enabled) {
      status.push('Disabled');
    } else if (agent.build) {
      const buildName = agent.build.buildType?.name || agent.build.buildTypeId;
      const runningInfo = agent.build['running-info'];
      let timeInfo = '';
      if (runningInfo) {
        const remaining = runningInfo.estimatedTotalSeconds - runningInfo.elapsedSeconds;
        timeInfo = formatTimeRemaining(remaining);
      }
      status.push(`Building: ${buildName}${timeInfo ? ` (${timeInfo})` : ''}`);
    } else {
      status.push('Idle');
    }

    return {
      name: agent.name,
      status: status.join(' - '),
      available: agent.connected && agent.enabled && !agent.build,
      building: !!agent.build
    };
  }

  // Handle custom parameter change
  function handleParameterChange(name, value) {
    setCustomParameters(prev => ({
      ...prev,
      [name]: value
    }));
  }

  // Reset a parameter back to its build default (drop the override).
  function resetParam(name) {
    setCustomParameters(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  // Toggle favorite parameter
  function toggleFavoriteParam(name) {
    setFavoriteParams(prev => {
      const newFavorites = prev.includes(name)
        ? prev.filter(p => p !== name)
        : [...prev, name];
      localStorage.setItem('favoriteParams', JSON.stringify(newFavorites));
      return newFavorites;
    });
  }

  // Get simple build name (strip path breadcrumbs)
  function getSimpleBuildName(name) {
    if (!name) return '';
    // Remove any path breadcrumbs like "Project / SubProject / BuildName"
    const parts = name.split(' / ');
    return parts[parts.length - 1] || name;
  }

  // Truncate description
  function truncateDescription(desc, maxLen = 50) {
    if (!desc) return '';
    if (desc.length <= maxLen) return desc;
    return desc.substring(0, maxLen) + '...';
  }

  if (loading) {
    return (
      <div className="run-build">
        <div className="run-build-header">
          <button onClick={onBack} className="back-button">
            &larr; Back to Dashboard
          </button>
          <h2>Run Build</h2>
        </div>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="run-build">
      <div className="run-build-header">
        <button onClick={onBack} className="back-button">
          &larr; Back to Dashboard
        </button>
        <h2>Run Build</h2>
      </div>

      {error && <div className="run-build-error">{error}</div>}
      {success && <div className="run-build-success">{success}</div>}

      <form onSubmit={handleSubmit} className="run-build-form">
        {/* Tabs */}
        <div className="run-build-tabs">
          <button
            type="button"
            className={`run-build-tab ${activeTab === 'build' ? 'active' : ''}`}
            onClick={() => setActiveTab('build')}
          >
            Build
          </button>
          <button
            type="button"
            className={`run-build-tab ${activeTab === 'parameters' ? 'active' : ''}`}
            onClick={() => setActiveTab('parameters')}
            disabled={!selectedBuildType}
          >
            Parameters {parameters.length > 0 ? `(${parameters.length})` : ''}
          </button>
        </div>

        {activeTab === 'build' && (
          <>
        {/* Build Configuration Selection */}
        <div className="form-group">
          <label htmlFor="buildType">Build Configuration</label>
          <select
            id="buildType"
            value={selectedBuildType}
            onChange={(e) => setSelectedBuildType(e.target.value)}
            required
          >
            <option value="">Select a build configuration...</option>
            {Object.entries(groupedOptions)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([group, opts]) => (
                <optgroup key={group} label={group}>
                  {opts.map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.text}
                    </option>
                  ))}
                </optgroup>
              ))}
          </select>
        </div>

        {/* Include Changes (Base CL) */}
            <div className="form-group">
              <label htmlFor="baseCL">Include changes</label>
              <div className="cl-combobox" ref={baseCLComboboxRef}>
                <input
                  type="text"
                  id="baseCL"
                  value={baseCLInputFocused
                    ? baseCL
                    : (baseCL && baseCLDescription
                        ? `${baseCL} - ${truncateDescription(baseCLDescription, 50)}${baseCLUser ? ` · ${baseCLUser}` : ''}`
                        : baseCL)}
                  onChange={(e) => handleBaseCLInputChange(e.target.value)}
                  onFocus={() => { setBaseCLInputFocused(true); setShowBaseCLDropdown(true); }}
                  onBlur={() => setBaseCLInputFocused(false)}
                  placeholder="Latest changes at build start"
                  autoComplete="off"
                />
                {showBaseCLDropdown && filteredRecentCLs.length > 0 && (
                  <div className="cl-dropdown">
                    {filteredRecentCLs.map(cl => (
                      <div
                        key={cl.changelist}
                        className={`cl-option ${cl.changelist === baseCL ? 'selected' : ''}`}
                        onClick={() => handleBaseCLSelect(cl)}
                      >
                        <span className="cl-desc" title={cl.description}>{cl.description}</span>
                        {cl.user && <span className="cl-user">{cl.user}</span>}
                        <span className="cl-number">{cl.changelist}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {loadingRecent && (
                <div className="loading-inline">Loading recent changelists...</div>
              )}
              <p className="form-help">Leave empty to use latest, or select a CL to build against</p>
            </div>

            {/* Shelved Changelist */}
            <div className="form-group">
              <label htmlFor="shelvedCL">Shelved Changelist</label>
              <div className="cl-combobox" ref={clComboboxRef}>
                <input
                  type="text"
                  id="shelvedCL"
                  value={clInputFocused
                    ? selectedShelvedCL
                    : (selectedShelvedCL && shelvedCLDescription
                        ? `${selectedShelvedCL} - ${truncateDescription(shelvedCLDescription, 60)}`
                        : selectedShelvedCL)}
                  onChange={(e) => handleCLInputChange(e.target.value)}
                  onFocus={() => { setCLInputFocused(true); setCLFilterText(''); setShowCLDropdown(true); }}
                  onBlur={() => setCLInputFocused(false)}
                  placeholder="Type CL # or select from your shelved list"
                  autoComplete="off"
                />
                {showCLDropdown && filteredShelvedCLs.length > 0 && (
                  <div className="cl-dropdown">
                    {filteredShelvedCLs.map(cl => (
                      <div
                        key={cl.changelist}
                        className={`cl-option ${cl.changelist === selectedShelvedCL ? 'selected' : ''}`}
                        onClick={() => handleCLSelect(cl)}
                      >
                        <span className="cl-desc" title={cl.description}>{cl.description}</span>
                        {cl.user && <span className="cl-user">{cl.user}</span>}
                        <span className="cl-number">{cl.changelist}</span>
                        {cl.fileCount != null && (
                          <span className="cl-filecount">
                            {cl.fileCount} {cl.fileCount === 1 ? 'file' : 'files'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {loadingShelved && (
                <div className="loading-inline">Loading your shelved changelists...</div>
              )}
              {!loadingShelved && primaryVcsStream && shelvedChangelists.length > 0 && (
                <div className="stream-filter-info">
                  Showing {streamFilteredCLs.length} of {shelvedChangelists.length} CLs matching stream: {getStreamName(primaryVcsStream)}*
                </div>
              )}
              <button
                type="button"
                className="refresh-button"
                onClick={() => loadShelvedChangelists(primaryVcsStream)}
                disabled={loadingShelved}
              >
                Refresh
              </button>
            </div>

        {/* Base Image selector - admin-only, used with "Build & Save Image" */}
        {isAdmin && (
          <div className="form-group">
            <label htmlFor="base-image">Base Image</label>
            <select
              id="base-image"
              value={baseImage}
              onChange={e => setBaseImage(e.target.value)}
            >
              <option value="">Select a base image...</option>
              {availableImages.map(img => (
                <option key={img.full_name} value={img.full_name}>
                  {img.name}:{img.tag}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Run on Orka VM */}
        <div className="form-group">
          <label>Run on Orka VM</label>
          {buildMachines.length === 0 ? (
            <p className="form-help">No Orka VMs available - pick a build agent below, or deploy one.</p>
          ) : (
            <div className="vm-list">
              {buildMachines.map(vm => {
                const ready = !!(vm.agent && vm.agent.connected);
                const isSel = selectedVM && selectedVM.name === vm.name;
                return (
                  <button
                    type="button"
                    key={vm.name}
                    className={`vm-option ${isSel ? 'selected' : ''}`}
                    onClick={() => setSelectedVM(isSel ? null : vm)}
                    disabled={!ready}
                    title={ready ? '' : 'TeamCity agent not connected on this VM yet'}
                  >
                    <span className="vm-name">{vm.name}</span>
                    <span className="vm-status">{vm.status}</span>
                    <span className={`vm-agent ${ready ? 'ready' : 'unavailable'}`}>
                      {ready ? 'Ready' : 'Agent not connected'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {selectedVM && (
            <p className="form-help">
              Pinning to <strong>{selectedVM.name}</strong>.{' '}
              <button type="button" className="link-button" onClick={() => setSelectedVM(null)}>Clear</button>
            </p>
          )}
          <button type="button" className="refresh-button" onClick={() => setShowDeployModal(true)}>
            + Deploy VM
          </button>
        </div>

        {/* Agent Selection */}
        <div className="form-group">
          <label htmlFor="agent">Build Agent</label>
          <select
            id="agent"
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            disabled={!!selectedVM}
          >
            <option value="">Any available agent</option>
            {(() => {
              const sortedAgents = [...agents].sort((a, b) => a.name.localeCompare(b.name));
              const enabledAgents = sortedAgents.filter(a => a.connected && a.enabled);
              const disabledAgents = sortedAgents.filter(a => !a.connected || !a.enabled);

              return (
                <>
                  {enabledAgents.length > 0 && (
                    <optgroup label="Enabled">
                      {enabledAgents.map(agent => {
                        const info = getAgentDisplayInfo(agent);
                        return (
                          <option key={agent.id} value={agent.id}>
                            {info.name} - {info.status}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  {isAdmin && disabledAgents.length > 0 && (
                    <optgroup label="Disabled">
                      {disabledAgents.map(agent => {
                        const info = getAgentDisplayInfo(agent);
                        return (
                          <option key={agent.id} value={agent.id}>
                            {info.name} - {info.status}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                </>
              );
            })()}
          </select>
          <p className="form-help">Select a specific agent or let TeamCity choose</p>
        </div>

        {/* Notification Toggle */}
        <div className="form-group notification-group">
          <label className="notification-toggle">
            <button
              type="button"
              className={`bell-button ${notifyOnComplete ? 'active' : ''}`}
              onClick={() => setNotifyOnComplete(!notifyOnComplete)}
              title={notifyOnComplete ? 'Notifications enabled' : 'Notifications disabled'}
            >
              {notifyOnComplete ? '🔔' : '🔕'}
            </button>
            <span>Notify me when build completes</span>
          </label>
        </div>
          </>
        )}

        {activeTab === 'parameters' && (
          <div className="parameters-tab">
            {!selectedBuildType ? (
              <div className="parameters-placeholder">
                Please select a build configuration first
              </div>
            ) : loadingParameters ? (
              <div className="loading-inline">Loading parameters...</div>
            ) : parameters.length === 0 ? (
              <div className="parameters-placeholder">
                No configurable parameters for this build
              </div>
            ) : (() => {
              const favoriteParameters = parameters.filter(p => favoriteParams.includes(p.name));
              const otherParameters = parameters.filter(p => !favoriteParams.includes(p.name));

              const renderParam = (param) => {
                const isCheckbox = param.type && param.type.toLowerCase().includes('checkbox');
                const customValue = customParameters[param.name];
                const isModified = customValue !== undefined && customValue !== '' && customValue !== param.value;
                const isFavorite = favoriteParams.includes(param.name);

                return (
                  <div key={param.name} className={`parameter-item ${isModified ? 'modified' : ''}`}>
                    <div className="param-head">
                      <button
                        type="button"
                        className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                        onClick={() => toggleFavoriteParam(param.name)}
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {isFavorite ? '❤️' : '🤍'}
                      </button>
                      <label className="param-name" htmlFor={`param-${param.name}`} title={param.name}>
                        {param.name}
                      </label>
                    </div>
                    <div className="param-value">
                      {isCheckbox ? (
                        <input
                          type="checkbox"
                          id={`param-${param.name}`}
                          checked={customValue !== undefined
                            ? customValue === 'true'
                            : param.value === 'true'}
                          onChange={(e) => handleParameterChange(param.name, e.target.checked ? 'true' : 'false')}
                        />
                      ) : (
                        <input
                          type="text"
                          id={`param-${param.name}`}
                          value={customValue || ''}
                          onChange={(e) => handleParameterChange(param.name, e.target.value)}
                          placeholder={param.value || '(empty)'}
                        />
                      )}
                    </div>
                    {isModified && (
                      <button
                        type="button"
                        className="reset-btn"
                        onClick={() => resetParam(param.name)}
                        title="Reset to default"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                );
              };

              return (
                <div className="parameters-list">
                  <p className="parameters-help">
                    Override build parameters below. Leave empty to use default values.
                  </p>
                  {favoriteParameters.length > 0 && (
                    <div className="parameters-section">
                      <div className="parameters-section-header">Favorites</div>
                      {favoriteParameters.map(renderParam)}
                    </div>
                  )}
                  {otherParameters.length > 0 && (
                    <div className="parameters-section">
                      {favoriteParameters.length > 0 && (
                        <div className="parameters-section-header">Other Parameters</div>
                      )}
                      {otherParameters.map(renderParam)}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting || !selectedBuildType}
          >
            {submitting ? 'Queuing Build...' : 'Run Build'}
          </button>
          {isAdmin && (
            <button
              type="button"
              className="submit-button image-build-button"
              disabled={imageBuildSubmitting || !selectedBuildType || !baseImage}
              onClick={handleStartImageBuild}
            >
              {imageBuildSubmitting ? 'Starting...' : 'Build & Save Image'}
            </button>
          )}
          <button
            type="button"
            className="cancel-button"
            onClick={onBack}
          >
            Cancel
          </button>
        </div>
      </form>

      {showDeployModal && (
        <QueryClientProvider client={orkaQueryClient}>
          <CreateVMModal
            onClose={() => setShowDeployModal(false)}
            onSuccess={() => { setShowDeployModal(false); loadBuildMachines(); }}
          />
        </QueryClientProvider>
      )}

      {imageBuildJob && (
        <div
          role="dialog"
          aria-label="Image Build in Progress"
          className="image-build-modal"
        >
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Image Build in Progress</h2>
              <div className="build-steps">
                {['queued','deploying-vm','waiting-for-agent','authorizing-agent',
                  'building','snapshotting','cleaning-up','done'].map(step => (
                  <div
                    key={step}
                    className={`build-step ${imageBuildJob.step === step ? 'active' : ''} ${
                      ['done','failed'].includes(imageBuildJob.step) &&
                      ['queued','deploying-vm','waiting-for-agent','authorizing-agent',
                       'building','snapshotting','cleaning-up'].indexOf(step) <
                      ['queued','deploying-vm','waiting-for-agent','authorizing-agent',
                       'building','snapshotting','cleaning-up','done'].indexOf(imageBuildJob.step)
                        ? 'complete' : ''
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
              {imageBuildJob.buildUrl && (
                <p>
                  <a href={imageBuildJob.buildUrl} target="_blank" rel="noreferrer">
                    View Build
                  </a>
                </p>
              )}
              {imageBuildJob.step === 'done' && imageBuildJob.imageTag && (
                <p className="image-build-result">
                  New image: <strong>{imageBuildJob.imageTag}</strong>
                </p>
              )}
              {imageBuildJob.step === 'failed' && imageBuildJob.error && (
                <p className="image-build-error">{imageBuildJob.error}</p>
              )}
              {(imageBuildJob.step === 'done' || imageBuildJob.step === 'failed') && (
                <button
                  type="button"
                  onClick={() => { setImageBuildJob(null); setImageBuildJobId(null); }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RunBuild;
